import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { PageShell } from '../components/PageShell';
import type { LoginEvento } from '../types';

/**
 * Segurança — trilha de acesso do escritório (login_eventos). Mostra quem entrou,
 * quando, de onde (IP + cidade/país, se geolocalizado) e o veredito da checagem de
 * "viagem impossível". Read-only; restrito a admin/gerente. Complementa a sessão
 * única: aqui o gestor enxerga acessos anômalos sinalizados (modelo "flag").
 */
function VeredictoBadge({ ev }: { ev: LoginEvento }) {
  const v = ev.veredito || (ev.anomalia ? 'flag' : 'ok');
  const mapa: Record<string, { txt: string; cls: string }> = {
    ok:      { txt: 'OK',          cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25' },
    sem_geo: { txt: 'sem geo',     cls: 'bg-slate-500/15 text-slate-500 dark:text-slate-400 border-slate-500/25' },
    flag:    { txt: '⚠ anomalia',  cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' },
    block:   { txt: '⛔ bloqueado', cls: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30' },
    erro:    { txt: 'erro',        cls: 'bg-slate-500/15 text-slate-500 dark:text-slate-400 border-slate-500/25' },
  };
  const s = mapa[v] ?? mapa.ok;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium ${s.cls}`}>{s.txt}</span>;
}

function quando(iso: string): string {
  try {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

export function Seguranca() {
  const [soAnomalias, setSoAnomalias] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['seguranca-logins', soAnomalias],
    queryFn: () => api.listarLogins(soAnomalias, 200),
    refetchInterval: 60_000,
  });
  const lista = data ?? [];
  const anomalias = (data ?? []).filter((e) => e.anomalia).length;

  return (
    <PageShell titulo={<h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">🔐 Segurança — acessos</h1>}>
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Trilha de logins do escritório. <strong>Sessão única</strong> garante uma conta por vez;
          a checagem de <strong>viagem impossível</strong> sinaliza acessos de locais distantes em
          tempo curto. A geolocalização só aparece quando a base offline (GeoLite2) está instalada.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={soAnomalias} onChange={(e) => setSoAnomalias(e.target.checked)} />
            Só anomalias
          </label>
          {!soAnomalias && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {lista.length} login(s){anomalias > 0 ? ` · ${anomalias} anômalo(s)` : ''}
            </span>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Quando</th>
                <th className="px-3 py-2 font-medium">Usuário</th>
                <th className="px-3 py-2 font-medium">Método</th>
                <th className="px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium">Local</th>
                <th className="px-3 py-2 font-medium">Veredito</th>
                <th className="px-3 py-2 font-medium">Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Carregando…</td></tr>
              ) : lista.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  {soAnomalias ? 'Nenhuma anomalia registrada. 👍' : 'Nenhum login registrado ainda.'}
                </td></tr>
              ) : lista.map((ev) => (
                <tr key={ev.id}
                    className={`border-t border-slate-100 dark:border-slate-800/60 ${ev.anomalia ? 'bg-amber-500/5' : ''}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">{quando(ev.criado_em)}</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{ev.email ?? `#${ev.usuario_id ?? '?'}`}</td>
                  <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{ev.metodo ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">{ev.ip ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                    {ev.geo_ok ? [ev.cidade, ev.pais].filter(Boolean).join(', ') || '—'
                               : <span className="text-slate-400 italic">sem geo</span>}
                  </td>
                  <td className="px-3 py-2"><VeredictoBadge ev={ev} /></td>
                  <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 max-w-[18rem] truncate" title={ev.detalhe ?? ''}>{ev.detalhe ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}
